// API response types for Ventura Machina backend

import type { ImageMetadata, Vehicle, SearchFilters } from './index';

/**
 * Base API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: ApiError;
  meta?: ApiMeta;
}

/**
 * API error structure
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * API metadata (pagination, timing, etc.)
 */
export interface ApiMeta {
  page?: number;
  pageSize?: number;
  totalCount?: number;
  hasMore?: boolean;
  processingTimeMs?: number;
}

/**
 * POST /api/v1/search request
 */
export interface SearchApiRequest {
  query: string;
  vehicle_id?: string;
  make?: string;
  model?: string;
  filters?: {
    component?: string;
    condition?: string;
    phase?: string;
    date_range?: {
      start: string;
      end: string;
    };
  };
  limit?: number;
  offset?: number;
}

/**
 * POST /api/v1/search response
 */
export interface SearchApiResponse {
  query: string;
  results: ImageApiData[];
  total_count: number;
  has_more: boolean;
  processing_time_ms: number;
  suggested_filters?: SearchFilters;
}

/**
 * Image data from API (snake_case)
 */
export interface ImageApiData {
  id: string;
  vehicle_id: string;
  filename: string;
  url: string;
  thumbnail_url: string;
  medium_url: string;
  component: string;
  component_detail: string;
  condition: string;
  phase: string;
  confidence: number;
  tags: string[];
  captured_at: string;
  classified_at: string;
  before_image_id?: string;
  after_image_id?: string;
  is_hero_image: boolean;
}

/**
 * GET /api/v1/showcase/:vehicleId response
 */
export interface ShowcaseApiResponse {
  vehicle: VehicleApiData;
  hero_images: ImageApiData[];
}

/**
 * Vehicle data from API (snake_case)
 */
export interface VehicleApiData {
  id: string;
  name: string;
  year: number;
  make: string;
  model: string;
  vin?: string;
  owner?: string;
  image_count: number;
  hero_image_url?: string;
  created_at: string;
  updated_at: string;
}

/**
 * GET /api/v1/compare/:id1/:id2 response
 */
export interface CompareApiResponse {
  before_image: ImageApiData;
  after_image: ImageApiData;
  component: string;
  similarity_score: number;
}

/**
 * GET /api/v1/vehicles response
 */
export interface VehiclesApiResponse {
  vehicles: VehicleApiData[];
  total_count: number;
}

/**
 * Transform API image data to domain model
 */
export function transformImageData(data: ImageApiData): ImageMetadata {
  return {
    id: data.id,
    vehicleId: data.vehicle_id,
    filename: data.filename,
    url: data.url,
    thumbnailUrl: data.thumbnail_url,
    mediumUrl: data.medium_url,
    component: data.component as ImageMetadata['component'],
    componentDetail: data.component_detail,
    condition: data.condition as ImageMetadata['condition'],
    phase: data.phase as ImageMetadata['phase'],
    confidence: data.confidence,
    tags: data.tags,
    capturedAt: data.captured_at,
    classifiedAt: data.classified_at,
    beforeImageId: data.before_image_id,
    afterImageId: data.after_image_id,
    isHeroImage: data.is_hero_image,
  };
}

/**
 * Transform API vehicle data to domain model
 */
export function transformVehicleData(data: VehicleApiData): Vehicle {
  return {
    id: data.id,
    name: data.name,
    year: data.year,
    make: data.make,
    model: data.model,
    vin: data.vin,
    owner: data.owner,
    imageCount: data.image_count,
    heroImageUrl: data.hero_image_url,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}
